import { useCallback, useEffect, useState } from 'react';
import type { Api, PasswordLinkResult, SuDeletedOrgRow, SuOrgRow } from '../../api';
import { fmtDateTime } from '../../admin/format';
import { PasswordLinkNotice } from '../PasswordLinkNotice';
import { alertAction, confirmAction } from '../../ui/confirm';
import { ORG_STATUS_LABEL } from './labels';
import { EMAIL_CHECK_REQUIRED_MESSAGE } from '../../emailRules';
import { EmailCheckField } from '../EmailCheckField';
import { useEmailCheck } from '../useEmailCheck';
import { SessionLimitInput } from '../SessionLimitInput';
import { SESSION_LIMIT_HINT, parseSessionLimit, sessionLimitInput, sessionLimitText } from '../../admin/sessionLimit';

/**
 * 새 조직 폼의 빈 값 — 조직 이름 + 첫 관리자(이메일 = 로그인 ID).
 * 비밀번호 칸은 없다(§14.19.9, 2026-09-28) — 관리자가 초대 메일 링크로 직접 정한다.
 */
const EMPTY_FORM = { orgName: '', email: '', userName: '' };

/** 단말기가 남은 조직의 삭제 안내(화면 선검사와 서버 409 가 같은 말을 하게 한 곳에). */
const orgHasDevicesMessage = (devices: number) =>
  `이 조직에 등록된 단말기가 ${devices}대 있습니다.\n[들어가기] → 기기에서 모두 삭제(조직에서 제거)한 뒤 다시 진행하세요.`;

/**
 * 조직 관리(수퍼유저, §14.19.1) — 목록 · 생성(첫 관리자 함께) · 이름 변경 · 정지/재개 · 삭제/되살리기.
 * 삭제는 **삭제 표시**다(§14.19.10, 2026-10-12) — 실제 데이터는 남고 아래 "삭제된 조직" 에서 되살린다(정지 상태로).
 * 조직 안의 사용자·기기는 [들어가기] 로.
 */
export function SuOrgsView({ api, onEnterOrg }: { api: Api; onEnterOrg: (orgId: string) => void }) {
  const [orgs, setOrgs] = useState<SuOrgRow[]>([]);
  const [deletedOrgs, setDeletedOrgs] = useState<SuDeletedOrgRow[]>([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  // 새 조직 첫 관리자 초대 결과(메일이 안 나갔으면 링크를 보여 준다).
  const [invite, setInvite] = useState<{ who: string; email: string; result: PasswordLinkResult } | null>(null);
  const [busyId, setBusyId] = useState('');
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  // 첫 관리자 이메일 형식 + [중복 확인](2026-09-29) — 통과해야 [만들기] 가 켜진다.
  const emailCheck = useEmailCheck(api, form.email);
  const resetEmailCheck = emailCheck.reset;
  const closeAdd = useCallback(() => {
    setAdding(false);
    setForm(EMPTY_FORM);
    resetEmailCheck();
  }, [resetEmailCheck]);
  const [renamingId, setRenamingId] = useState('');
  const [renameValue, setRenameValue] = useState('');
  // 동시 세션 상한(§15) 편집 중인 조직 + 입력 글자(빈 칸 = 무제한). 이름 변경과 같은 행 안 편집.
  const [limitId, setLimitId] = useState('');
  const [limitValue, setLimitValue] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [live, deleted] = await Promise.all([api.suOrgs(), api.suDeletedOrgs()]);
      setOrgs(live);
      setDeletedOrgs(deleted);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /** 행 조작 공통 — 잠금 → 실행 → 알림 → 재조회 → 오류(사용자 화면과 같은 흐름). */
  const run = useCallback(
    async (id: string, action: () => Promise<string>) => {
      setBusyId(id);
      setError('');
      setNotice('');
      setInvite(null);
      try {
        setNotice(await action());
        await refresh();
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setBusyId('');
      }
    },
    [refresh],
  );

  const submitAdd = useCallback(async () => {
    const orgName = form.orgName.trim();
    const email = form.email.trim();
    const userName = form.userName.trim();
    if (!orgName || !email || !userName) {
      setError('조직 이름과 첫 관리자의 이메일·이름을 입력하세요.');
      return;
    }
    // 형식·중복은 [중복 확인] 이 통과시킨 값만(버튼이 꺼져 있어 보통은 여기 안 온다 — 다른 경로 방어).
    if (!emailCheck.view.confirmed) {
      emailCheck.touch();
      setError(emailCheck.view.formatOk ? EMAIL_CHECK_REQUIRED_MESSAGE : emailCheck.view.message || '이메일 형식이 아닙니다.');
      return;
    }
    await run('__add__', async () => {
      const r = await api.suCreateOrg({ orgName, admin: { email, userName } });
      closeAdd();
      if (r.invite) setInvite({ who: userName, email: r.admin.email, result: r.invite });
      // 초대를 다시 보내려면 그 조직에 [들어가기] → 사용자 → [초대 다시 보내기].
      return `'${r.orgName}' 조직을 만들었습니다. 초대 링크를 다시 보내야 하면 [들어가기] → 사용자 화면에서 보낼 수 있습니다.`;
    });
  }, [api, closeAdd, emailCheck, form, run]);

  const submitRename = useCallback(
    async (o: SuOrgRow) => {
      const name = renameValue.trim();
      if (!name || name === o.orgName) {
        setRenamingId('');
        return;
      }
      await run(o.orgId, async () => {
        await api.suUpdateOrg(o.orgId, { orgName: name });
        setRenamingId('');
        return `조직 이름을 '${name}' 으로 바꿨습니다.`;
      });
    },
    [api, renameValue, run],
  );

  /** 동시 세션 상한 저장(§15). 그대로면 닫기만. 낮춰도 열린 세션은 끊지 않는다(서버) — 안내에 적는다. */
  const submitLimit = useCallback(
    async (o: SuOrgRow) => {
      const parsed = parseSessionLimit(limitValue);
      if (!parsed.ok) {
        setError(parsed.error);
        return;
      }
      if (parsed.value === (o.maxConcurrentSessions ?? null)) {
        setLimitId('');
        return;
      }
      await run(o.orgId, async () => {
        const r = await api.suUpdateOrg(o.orgId, { maxConcurrentSessions: parsed.value });
        setLimitId('');
        return `'${r.orgName}' 동시 세션 상한을 ${sessionLimitText(r.maxConcurrentSessions)}(으)로 바꿨습니다. 다음 접속부터 적용됩니다.`;
      });
    },
    [api, limitValue, run],
  );

  /** 정지는 파급이 크다 — 확인을 받는다(재개는 바로). */
  const toggleStatus = useCallback(
    async (o: SuOrgRow) => {
      const disable = o.status === 'active';
      if (disable) {
        const ok = await confirmAction({
          title: `'${o.orgName}' 조직을 정지할까요?`,
          message:
            '이 조직의 모든 사용자가 로그인할 수 없게 되고, 지금 열린 창과 진행 중인 원격 세션이 즉시 끊깁니다.\n' +
            '기기·사용자·기록은 그대로 남으며, 다시 재개할 수 있습니다(재개 후 사용자들은 다시 로그인해야 합니다).',
          confirmLabel: '정지',
          danger: true,
        });
        if (!ok) return;
      }
      await run(o.orgId, async () => {
        const r = await api.suUpdateOrg(o.orgId, { disabled: disable });
        if (!disable) return `'${r.orgName}' 조직을 재개했습니다.`;
        const c = r.cutOff;
        return `'${r.orgName}' 조직을 정지했습니다(사용자 ${c.users}명 · 로그인 ${c.revokedLogins}건 · 원격 세션 ${c.closedSessions}건 종료).`;
      });
    },
    [api, run],
  );

  /**
   * 삭제(삭제 표시) — ① 단말기가 남았으면 알리고 끝(서버도 409 로 막는다 — 목록이 낡았을 때)
   * ② 조직 이름을 똑같이 입력해야 진행 ③ 결과 안내.
   */
  const deleteOrg = useCallback(
    async (o: SuOrgRow) => {
      if (o.devices > 0) {
        await alertAction(`'${o.orgName}' 조직을 삭제할 수 없습니다.`, orgHasDevicesMessage(o.devices));
        return;
      }
      const ok = await confirmAction({
        title: `'${o.orgName}' 조직을 삭제할까요?`,
        message:
          `이 조직의 사용자 ${o.users}명이 모두 로그인할 수 없게 되고, 지금 열린 창과 원격 세션이 즉시 끊깁니다.\n` +
          '사용자 이메일은 풀려서 다른 조직에서 다시 쓸 수 있습니다.\n' +
          '사용자·그룹·기록은 보관되며, 아래 "삭제된 조직" 에서 되살릴 수 있습니다(정지 상태로 돌아옵니다).',
        confirmLabel: '삭제',
        danger: true,
        requireText: o.orgName,
      });
      if (!ok) return;
      await run(o.orgId, async () => {
        const r = await api.suDeleteOrg(o.orgId, o.orgName);
        return `'${r.orgName}' 조직을 삭제했습니다(사용자 ${r.cutOff.users}명 막힘). 아래 "삭제된 조직" 에서 되살릴 수 있습니다.`;
      });
    },
    [api, run],
  );

  /** 되살리기 — 정지 상태로 돌아온다(사용자들은 [재개] 후에 쓴다). */
  const restoreOrg = useCallback(
    async (o: SuDeletedOrgRow) => {
      const ok = await confirmAction({
        title: `'${o.orgName}' 조직을 되살릴까요?`,
        message: '정지 상태로 돌아옵니다. 확인한 뒤 [재개] 하면 사용자들이 다시 로그인할 수 있습니다.',
        confirmLabel: '되살리기',
      });
      if (!ok) return;
      await run(o.orgId, async () => {
        const r = await api.suRestoreOrg(o.orgId);
        const base = `'${r.orgName}' 조직을 되살렸습니다(정지 상태). 확인 후 [재개] 하세요.`;
        const conflicts = r.emailConflicts ?? [];
        if (conflicts.length === 0) return base;
        // 원래 이메일을 다른 계정이 쓰게 된 사용자 — 비활성으로 되살아났다. 그 조직 [사용자] 에서 [이메일 지정].
        const who = conflicts.map((c) => `${c.userName}(${c.originalEmail})`).join(', ');
        return `${base}\n⚠ 이메일이 다른 계정과 겹쳐 비활성으로 되살린 사용자 ${conflicts.length}명: ${who} — [들어가기] → 사용자에서 [이메일 지정] 하세요.`;
      });
    },
    [api, run],
  );

  return (
    <div className="view">
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>조직</h2>
        <div className="row">
          <button onClick={() => (adding ? closeAdd() : setAdding(true))}>{adding ? '추가 닫기' : '조직 만들기'}</button>
          <button className="ghost" onClick={() => void refresh()} disabled={loading}>
            {loading ? '불러오는 중…' : '새로고침'}
          </button>
        </div>
      </div>

      {adding && (
        <div className="enroll-panel">
          <div className="name" style={{ marginBottom: 10 }}>새 조직 + 첫 관리자</div>
          <div className="form-grid">
            <label className="field">
              <span>조직 이름</span>
              <input value={form.orgName} onChange={(e) => setForm({ ...form, orgName: e.target.value })} maxLength={150} />
            </label>
            <label className="field field-wide">
              <span>관리자 이메일(로그인 ID)</span>
              <EmailCheckField
                value={form.email}
                onChange={(email) => setForm({ ...form, email })}
                emailCheck={emailCheck}
                placeholder="admin@example.com"
              />
            </label>
            <label className="field">
              <span>관리자 이름</span>
              <input value={form.userName} onChange={(e) => setForm({ ...form, userName: e.target.value })} maxLength={100} />
            </label>
          </div>
          <div className="hint" style={{ marginBottom: 10 }}>
            만들면 관리자 이메일로 <b>초대 메일</b>이 갑니다. 관리자가 메일의 링크에서 비밀번호를 직접 정한 뒤 로그인합니다
            (링크 72시간 유효).
          </div>
          <div className="row">
            <button onClick={() => void submitAdd()} disabled={busyId === '__add__' || !emailCheck.view.confirmed}>
              {busyId === '__add__' ? '만드는 중…' : '만들기'}
            </button>
            <button className="ghost" onClick={closeAdd}>
              취소
            </button>
            {!emailCheck.view.confirmed && <span className="hint">관리자 이메일 [중복 확인] 후 만들 수 있습니다.</span>}
          </div>
        </div>
      )}

      {error && <div className="hint" style={{ color: 'var(--err)', marginBottom: 12 }}>{error}</div>}
      {notice && <div className="hint" style={{ marginBottom: 12, whiteSpace: 'pre-line' }}>{notice}</div>}
      {invite && <PasswordLinkNotice {...invite} onClose={() => setInvite(null)} />}

      {orgs.length === 0 && !loading ? (
        <p className="hint">조직이 없습니다.</p>
      ) : (
        <div className="list">
          {orgs.map((o) => {
            const busy = busyId === o.orgId;
            const disabled = o.status !== 'active';
            return (
              <div key={o.orgId} className="list-item" style={{ cursor: 'default', ...(disabled ? { opacity: 0.65 } : {}) }}>
                <span className={`dot ${disabled ? 'err' : 'ok'}`} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  {renamingId === o.orgId ? (
                    <div className="row">
                      <input
                        autoFocus
                        value={renameValue}
                        maxLength={150}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void submitRename(o);
                          if (e.key === 'Escape') setRenamingId('');
                        }}
                        aria-label="조직 이름"
                      />
                      <button onClick={() => void submitRename(o)} disabled={busy}>저장</button>
                      <button className="ghost" onClick={() => setRenamingId('')}>취소</button>
                    </div>
                  ) : (
                    <div className="name">
                      {o.orgName}
                      {disabled && <span className="badge err" style={{ marginLeft: 8 }}>{ORG_STATUS_LABEL[o.status] ?? o.status}</span>}
                    </div>
                  )}
                  <div className="sub">
                    사용자 {o.users}(관리자 {o.admins}) · 기기 {o.devices}(온라인 {o.onlineDevices}) · 만든 날 {fmtDateTime(o.createdAt)}
                    {/* 열린 세션 / 상한 — 상한 판정과 같은 기준(로그인 세션, 수퍼유저 제외). 옛 서버는 칸이 없다. */}
                    {o.openSessions !== undefined && (
                      <> · 동시 세션 {o.openSessions}/{sessionLimitText(o.maxConcurrentSessions)}</>
                    )}
                  </div>
                  {limitId === o.orgId && (
                    <div className="row" style={{ flexWrap: 'wrap', marginTop: 6 }}>
                      <span className="hint">동시 세션 상한</span>
                      <SessionLimitInput
                        autoFocus
                        value={limitValue}
                        onChange={setLimitValue}
                        onEnter={() => void submitLimit(o)}
                        onEscape={() => setLimitId('')}
                        label="조직 동시 세션 상한"
                      />
                      <button onClick={() => void submitLimit(o)} disabled={busy}>저장</button>
                      <button className="ghost" onClick={() => setLimitId('')}>취소</button>
                      <span className="hint">{SESSION_LIMIT_HINT} 사용자별 상한은 [들어가기] → 사용자 편집에서 정합니다.</span>
                    </div>
                  )}
                </div>
                <div className="actions">
                  <button onClick={() => onEnterOrg(o.orgId)} disabled={busy}>
                    들어가기
                  </button>
                  <button
                    className="ghost"
                    onClick={() => {
                      setRenamingId(o.orgId);
                      setRenameValue(o.orgName);
                    }}
                    disabled={busy}
                  >
                    이름 변경
                  </button>
                  <button
                    className={`ghost ${limitId === o.orgId ? 'on' : ''}`}
                    onClick={() => {
                      if (limitId === o.orgId) {
                        setLimitId('');
                        return;
                      }
                      setLimitId(o.orgId);
                      setLimitValue(sessionLimitInput(o.maxConcurrentSessions));
                    }}
                    disabled={busy}
                  >
                    세션 상한
                  </button>
                  <button className={`ghost ${disabled ? '' : 'danger'}`} onClick={() => void toggleStatus(o)} disabled={busy}>
                    {busy ? '처리 중…' : disabled ? '재개' : '정지'}
                  </button>
                  <button className="ghost danger" onClick={() => void deleteOrg(o)} disabled={busy}>
                    삭제
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 삭제된 조직(§14.19.10) — 평소엔 접어 둔다. 실수로 지운 조직을 되살리는 곳. */}
      {deletedOrgs.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <button className="ghost" onClick={() => setShowDeleted((v) => !v)} aria-expanded={showDeleted}>
            {showDeleted ? '▾' : '▸'} 삭제된 조직 ({deletedOrgs.length})
          </button>
          {showDeleted && (
            <div className="list" style={{ marginTop: 8 }}>
              {deletedOrgs.map((o) => (
                <div key={o.orgId} className="list-item" style={{ cursor: 'default', opacity: 0.65 }}>
                  <span className="dot err" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="name">
                      {o.orgName}
                      <span className="badge err" style={{ marginLeft: 8 }}>{ORG_STATUS_LABEL.deleted}</span>
                    </div>
                    <div className="sub">
                      사용자 {o.users} · 삭제한 날 {fmtDateTime(o.deletedAt)}
                    </div>
                  </div>
                  <div className="actions">
                    <button onClick={() => void restoreOrg(o)} disabled={busyId === o.orgId}>
                      {busyId === o.orgId ? '처리 중…' : '되살리기'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
